import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import {
  TestCreditFlowDialogData,
  TestCreditFlowDialogResult,
  TestCreditRuleFn,
  TestCreditFlowFn,
  TestCreditFlowNodeFn,
  ApplicationSort,
  ApplicationTypes,
  StageNameType,
  SimplifiedApplication,
  CreditRuleMapping,
  NotNullable,
  ApplicationCreditFlowNodeDetail,
  Application,
  stringifyJSON,
  TestCreditRuleItemFn,
  CreditRuleItem,
  LocalApplicationDbReloadFn,
} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from "@angular/material/expansion";
import { CreditRuleComponent } from '../credit-component/credit-rule.component';
import { ApplicationDialogService } from './application-dialog.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { debounceTime, Subscription, tap } from 'rxjs';
import { createAsyncStore } from '@ngneat/loadoff';
import { UntilDestroy } from '@ngneat/until-destroy';
import moment from 'moment';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { CreditFlowDetailsComponent } from '../credit-component/credit-flow-details.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TagBoxComponent } from '../message-box/tag-box.component';

const MAX_DATE: number = moment().add(1, 'day').startOf('day').toDate().getTime();
@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './test-credit-flow.dialog.html',
  styleUrls: ['./test-credit-flow.dialog.scss'],
  standalone: true,
  imports: [MatFormFieldModule, MatInputModule, TagBoxComponent, ClipboardModule, MatCheckboxModule, CreditFlowDetailsComponent, MatAutocompleteModule, MatSelectModule, CreditRuleComponent, MatExpansionModule, FormsModule, ReactiveFormsModule, FlexModule, MatButtonModule, MatDialogModule, MatDividerModule, NgxJsonViewerModule]
})
export class TestCreditFlowDialog implements OnInit {
  formControlApplication!: FormControl<SimplifiedApplication | null>;
  type!: 'credit-flow' | 'credit-flow-node' | 'credit-rule' | 'credit-rule-item';
  testCreditRuleFn!: TestCreditRuleFn;
  testCreditFlowFn!: TestCreditFlowFn;
  testCreditFlowNodeFn!: TestCreditFlowNodeFn;
  testCreditRuleItemFn!: TestCreditRuleItemFn;
  localApplicationDbReloadFn!: LocalApplicationDbReloadFn;
  creditFlowId!: number;
  creditFlowNodeId!: number | null;
  creditRuleMapping!: CreditRuleMapping | null;
  creditRuleItem!: CreditRuleItem | null;
  store = createAsyncStore();
  subscriptions: Subscription[] = [];
  allApplications: SimplifiedApplication[] = [];
  MAX_DATE = MAX_DATE;
  filteredApplications: SimplifiedApplication[] = []; // Filtered list for search
  selectedApplicationNumber!: number; // Store selected application number
  formGroup!: FormGroup<{
    application: FormControl<SimplifiedApplication | null>,
  }>;

  results: ApplicationCreditFlowNodeDetail[] = [];
  sampleApplicationData!: Application | null;
  sampleApplicationString!: string;
  resultStatus!: string;

  applicationDisplayWithFn = (v: NotNullable<SimplifiedApplication> | null ) => {
    if (v) {
      return `${v?.BrokerAppId ?? ''} - ${v?.CompanyName} (${v?.ApplicationType}) - ${v?.StageName}`;
    }
    return '';
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: TestCreditFlowDialogData,
    private dialogRef: MatDialogRef<TestCreditFlowDialog, TestCreditFlowDialogResult>,
    private dialogService: ApplicationDialogService,
    private toastService: PortalHotToastService,
    private formBuilder: FormBuilder) {
    this.formControlApplication = this.formBuilder.control(null, [Validators.required]);
    this.type = data.type;
    this.testCreditRuleFn = this.data.testCreditRuleFn;
    this.testCreditFlowFn = this.data.testCreditFlowFn;
    this.testCreditFlowNodeFn = this.data.testCreditFlowNodeFn;
    this.testCreditRuleItemFn = this.data.testCreditRuleItemFn;
    this.localApplicationDbReloadFn = this.data.localApplicationDbReloadFn;
    this.creditFlowId = this.data.creditFlowId;
    this.creditFlowNodeId = this.data.creditFlowNodeId;
    this.creditRuleMapping = this.data.creditRuleMapping;
    this.creditRuleItem = this.data.creditRuleItem;
    this.formGroup = this.formBuilder.group({
      application: this.formControlApplication,
    });
  }

  ngOnInit() {
    this.reload();
    this.formControlApplication.valueChanges.pipe(debounceTime(300)).subscribe((searchTerm) => {
      this.filterApplications(searchTerm);
    });
  }

  filterApplications(searchTerm: any) {
    if (!searchTerm) {
      this.filteredApplications = this.allApplications; // Show full list if no search term
    } else {
      this.filteredApplications = this.allApplications.filter((app: { ApplicationId: { toString: () => string | any[]; }; CompanyName: string; BrokerAppId: string }) => 
        app.ApplicationId.toString().includes(searchTerm) || 
        app.CompanyName?.toLowerCase().includes(searchTerm) || 
        app?.BrokerAppId?.toLowerCase().includes(searchTerm)
      );
    }
  }

  selectApplication(application: SimplifiedApplication) {
    this.selectedApplicationNumber = application.ApplicationId; // Store selected app number
    this.formControlApplication.setValue(application, { emitEvent: false }); // Update input field
  }

  reload(
    forceReload: boolean = false,
    sorts?: ApplicationSort,
    search?: string,
    applicationType?: ApplicationTypes[],
    applicationStage?: StageNameType[],
    startDate?: Date,
    endDate?: Date,
    minDate?: Date,
    statistic?: string
  ) {
    this.store = createAsyncStore();
    // Remove pagination to fetch all applications
    this.subscriptions.push(this.localApplicationDbReloadFn({
      forceReload,
      page: { offset: 0, limit: Number.MAX_SAFE_INTEGER },
      filter: search,
      applicationStage,
      applicationType,
      sorts,
      dateFilter: { startDate, endDate: endDate ?? new Date(MAX_DATE) },
      minDate,
      statistic
    }).pipe(
      this.toastService.spinnerObservable(),
      tap((r: any) => {
        this.allApplications = r.applications; 
        this.filteredApplications = this.allApplications;
      }),
    ).subscribe());
  }

  onCancel() {
    this.dialogRef.close()
  }

  onEvaluate() {
    if (this.selectedApplicationNumber) {
      if (this.type === 'credit-rule-item') {
        this.testCreditRuleItemFn({
          applicationId: this.selectedApplicationNumber,
          creditRuleItem: this.creditRuleItem as CreditRuleItem
        }).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(response => {
          console.log('credit rule item testing: ', response);
          this.results = response.flow;
          this.resultStatus = response.status;
          this.sampleApplicationData = response.application;
          this.sampleApplicationString = stringifyJSON(this.sampleApplicationData);
        })
      } else if (this.type === 'credit-rule') {
        this.testCreditRuleFn({
          applicationId: this.selectedApplicationNumber,
          creditFlowId: this.creditFlowId,
          creditFlowNodeId: this.creditFlowNodeId as number,
          creditRuleMapping: this.creditRuleMapping as CreditRuleMapping,
        }).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(response => {
          console.log('credit rule testing: ', response);
          this.results = response.flow;
          this.resultStatus = response.status;
          this.sampleApplicationData = response.application;
          this.sampleApplicationString = stringifyJSON(this.sampleApplicationData);
        })
      } else if (this.type === 'credit-flow-node') {
        this.testCreditFlowNodeFn({
          applicationId: this.selectedApplicationNumber,
          creditFlowId: this.creditFlowId,
          creditFlowNodeId: this.creditFlowNodeId as number,
        }).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(response => {
          console.log('credit flow node testing: ', response);
          this.results = response.flow;
          this.resultStatus = response.status;
          this.sampleApplicationData = response.application;
          this.sampleApplicationString = stringifyJSON(this.sampleApplicationData);
        })
      } else {
        this.testCreditFlowFn({
          applicationId: this.selectedApplicationNumber,
          creditFlowId: this.creditFlowId,
        }).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(response => {
          console.log('credit flow testing: ', response);
          this.results = response.flow;
          this.resultStatus = response.status;
          this.sampleApplicationData = response.application;
          this.sampleApplicationString = stringifyJSON(this.sampleApplicationData);
        })
      }
    } else {
      this.dialogService.openAlertDialog({
        message: 'Error',
        subMessage: 'Please select an application'
      }).afterClosed().subscribe()
    }
  }

  onCopy() {
    this.toastService.snackbar("Application data has been copied into the clipboard");
  }
}
